import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

interface ServiceDetailItemProps {
  id: string;
  title: string;
  description: string;
  image: string;
  details: ReactNode;
  reversed?: boolean;
  label?: string;
}

export default function ServiceDetailItem({
  id,
  title,
  description,
  image,
  details,
  reversed = false,
  label,
}: ServiceDetailItemProps) {
  const { ref, visible } = useScrollAnimation();

  return (
    <div
      id={id}
      ref={ref}
      className={`grid grid-cols-1 lg:grid-cols-2 gap-0 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
    >
      {/* Image side */}
      <div className={`relative overflow-hidden min-h-[360px] lg:min-h-[480px] ${reversed ? 'lg:order-last' : ''}`}>
        <img
          src={image}
          alt={title}
          className="w-full h-full object-cover object-top absolute inset-0"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-stone-950/30" />
        {label && (
          <div className="absolute top-6 left-6">
            <span className="bg-accent text-white text-xs font-semibold px-3.5 py-1.5 rounded-full">{label}</span>
          </div>
        )}
      </div>

      {/* Content side */}
      <div className={`bg-white flex flex-col justify-center px-10 lg:px-14 py-14 lg:py-16 ${reversed ? 'lg:order-first' : ''}`}>
        <p className="text-accent text-xs font-semibold tracking-[0.2em] uppercase mb-4">{label || 'Service'}</p>
        <h3
          className="font-serif text-3xl lg:text-4xl text-stone-900 leading-tight mb-5"
          style={{ letterSpacing: '-0.01em' }}
        >
          {title}
        </h3>
        <p className="text-stone-500 text-base leading-relaxed mb-7">{description}</p>
        <div className="mb-8">{details}</div>
        <Link
          to="/about"
          className="inline-flex items-center gap-3 bg-stone-950 hover:bg-stone-800 text-white font-semibold px-6 py-3.5 rounded-lg transition-colors whitespace-nowrap group text-sm self-start"
        >
          Request a Consultation
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1 text-xs" />
          </div>
        </Link>
      </div>
    </div>
  );
}
